import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Switch, FormControlLabel, Box, Divider } from "@mui/material";
import { useState, useEffect } from "react";
import { IScheduler, SchedulerType } from "../../../../types/database/Scheduler";
import { updateSchedulerProperties } from "../../services/gutcheck-admin-api.service";
import { useAuth0 } from "@auth0/auth0-react";
import ClientProvider from "../../../../modules/ClientProvider";
import useAdminGetProjectData from "../../hooks/useAdminGetProjectData";

interface AdminSchedulerModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
    scheduler: IScheduler | null;
    projectTag: string;
    projectId: number;
    type: SchedulerType;
}

export const AdminSchedulerModal = ({
    open,
    onClose,
    onSubmit,
    projectTag,
    projectId,
    type
}: AdminSchedulerModalProps) => {
    const { getAccessTokenSilently } = useAuth0();
    const [scheduler, setScheduler] = useState<IScheduler | null>(null);
    const [formData, setFormData] = useState({
        internal_attendee: "",
        scheduling_url: "",
        is_active: false,
        is_default: false,
        is_available: false
    });
    const [schedulerProperties, setSchedulerProperties] = useState({
        name: "",
        subtitle: "",
        duration: "",
        host: "",
        description: ""
    });
    const [loading, setLoading] = useState(false);
    const schedulerClient = ClientProvider.provideSchedulerClient();
    const { project, reloadProjectData } = useAdminGetProjectData(projectId);

    useEffect(() => {
        const loadScheduler = async () => {
            if (!open) return;

            setLoading(true);
            try {
                const accessToken = await getAccessTokenSilently();
                const { data, error } = await schedulerClient.getSchedulerByType(projectTag, type, accessToken);

                if (data?.scheduler) {
                    setScheduler(data.scheduler);
                    setFormData({
                        internal_attendee: data.scheduler.internal_attendee || "",
                        scheduling_url: data.scheduler.scheduling_url || "",
                        is_active: data.scheduler.is_active,
                        is_default: data.scheduler.is_default,
                        is_available: data.scheduler.is_available
                    });
                }
            } catch (error) {
                console.error("Error loading scheduler:", error);
            } finally {
                setLoading(false);
            }
        };

        loadScheduler();
    }, [open, type, projectTag, getAccessTokenSilently]);

    useEffect(() => {
        if (project) {
            setSchedulerProperties({
                name: project.effective_scheduler_properties?.[type]?.name || "",
                subtitle: project.effective_scheduler_properties?.[type]?.subtitle || "",
                duration: project.effective_scheduler_properties?.[type]?.duration || "",
                host: project.effective_scheduler_properties?.[type]?.host || "",
                description: project.effective_scheduler_properties?.[type]?.description || ""
            });
        }
    }, [project, type]);

    const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSchedulerPropertiesChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setSchedulerProperties(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleSubmit = async () => {
        try {
            const accessToken = await getAccessTokenSilently();
            await schedulerClient.updateScheduler(projectTag, scheduler?.uuid || "", formData, accessToken);
            await updateSchedulerProperties(projectId, type, schedulerProperties, accessToken);
            onSubmit();
        } catch (error) {
            console.error("Error updating scheduler:", error);
        }
    };

    if (!scheduler) {
        return <div>Loading...</div>;
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <h5>
                {scheduler ? 'Edit' : 'Configure'} Scheduler: {type.charAt(0).toUpperCase() + type.slice(1)} {schedulerProperties.name}
            </h5>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        label="Internal Attendee"
                        value={formData.internal_attendee}
                        onChange={handleChange('internal_attendee')}
                        fullWidth
                    />
                    <TextField
                        label="Scheduling URL"
                        value={formData.scheduling_url}
                        onChange={handleChange('scheduling_url')}
                        fullWidth
                    />
                    <Divider sx={{ my: 1 }} />
                    <TextField
                        label="Custom Name"
                        value={schedulerProperties.name}
                        onChange={handleSchedulerPropertiesChange('name')}
                        fullWidth
                    />
                    <TextField
                        label="Subtitle"
                        value={schedulerProperties.subtitle}
                        onChange={handleSchedulerPropertiesChange('subtitle')}
                        fullWidth
                    />
                    <TextField
                        label="Custom Description"
                        value={schedulerProperties.description}
                        onChange={handleSchedulerPropertiesChange('description')}
                        multiline
                        rows={2}
                        fullWidth
                    />
                    <TextField
                        label="Custom Duration"
                        value={schedulerProperties.duration}
                        onChange={handleSchedulerPropertiesChange('duration')}
                        fullWidth
                    />
                    <TextField
                        label="Suggested Host"
                        value={schedulerProperties.host}
                        onChange={handleSchedulerPropertiesChange('host')}
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading}>
                    {loading ? 'Loading...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 